/* eslint-disable @typescript-eslint/no-unused-vars */
import { ReactElement, useEffect, useRef, useState } from 'react';
import { Form, Formik, FormikContext, FormikProps, getIn } from 'formik';
import FormContainer from '@pages/FormContainer';
import ReactGA from 'react-ga';
import { defaultForm, ReviewMetaData, ReviewSchema, iReviewFormValues, getSubmissionPayload } from '@services/ReviewService';
import { FormNavFooter, FormLoading, Alert, ProgressBar } from '@components/index';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { FormType } from '@constants';
import { iToolTips, iCountry, iApiError, iFormNavActions, iPartialFormData } from '@interfaces/interfaces';

import { getDateForValidateDebtor, getStringIndex, navigateToNextStep, setCurrentFormikValues, setFormDataValues, bytesToMBSize, navToError } from '@utils';

import ErrorContent from '@components/Content/AlertContent/ErrorContent';
import { getBasicInfo, getCountryList, getPartialForm, getTooltips, getValidFilesConfig, submitSavedPartialForm, savePartialForm, getObligationOptions } from '@api/index';

import VicFinesContext from '../VicFinesFormContext';
import { ReviewStep0, ReviewStep1, ReviewStep2, ReviewStep3, ReviewStep4, ReviewStep5, ReviewStep6, ReviewStep7, ReviewStep9, ReviewStep10, ReviewStep13, ReviewStep14, ReviewStep8, ReviewStep11, ReviewStep12, ReviewStep15, ReviewErrorPage } from '.';
import IssueIcon from '../../../images/issue.svg';

const {
  REACT_APP_SITECORE_API_ENDPOINT: API_URL,
  REACT_APP_PATH_REVIEW: PATH_REVIEW,
} = process.env;

function Review(): ReactElement {
  const formikValuesRef = useRef({} as FormikProps<iReviewFormValues>);
  const location = useLocation();
  const navigate = useNavigate();
  const pathElementArray = location.pathname.split('/');
  const stepIndex = getStringIndex(pathElementArray, 'step');
  const stepIndexDiff = stepIndex - pathElementArray.length;
  // example returned [step0,3224242] or [step0]
  const stepArray = pathElementArray.slice(stepIndexDiff);
  // example returned 0
  const stepIndexInPath = getStringIndex(stepArray, 'step');
  // always get the element contain step like step0
  const getStepInPath = stepArray[stepIndexInPath];
  const pathNameLessLastSegment = stepIndex ? location.pathname.split('/').slice(0, stepIndexDiff).join('/') : location.pathname;

  const currentStep = location.pathname === `${PATH_REVIEW}` || location.pathname === `${PATH_REVIEW}/` ? 'step0' : `${getStepInPath}`;

  const [loading, setLoading] = useState(true);
  const [countryList, setCountryList] = useState<iCountry[]>([]);
  const [tooltips, setTooltips] = useState<iToolTips>({} as iToolTips);
  const [apiError, setApiError] = useState<iApiError>({} as iApiError);
  const [activeError, setActiveError] = useState(false);

    const partialFormAttachmentFields = [
      'StatementAndEvidence',
      'Obligations',
      'selectTheOption',
      'reviewGroundsList',
      'selectTheSpecialOption',
      'EligibleObligations',
      'SelectedObligationsStep11',
      'SelectedObligationsStep12'
    ];
    const arrayFieldsToBeCleared = [
      'ActionsTakenOnFine',
      'obligations',
      'infringementFineArray',
      'enteredObligationsArray',
      'InfringementNums',
      'AgencyNames',
      'AOffence',
      'DAOffence',
    ];

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        const [countryResults, tooltipResults] = await Promise.all([getCountryList(), getTooltips('fines.ReviewForm')]);
        setCountryList(countryResults);
        setTooltips(tooltipResults);
        setLoading(false);
      } catch (error) {
        navToError(navigate, location);
      }
    };
    fetchData();
    const handlePopstate = () => {
      const isSubmitted = formikValuesRef.current && formikValuesRef.current.values ? formikValuesRef.current.values?.submitted : false;

      if (window.location.pathname === '/Request-a-review/Review-Form/step14' || isSubmitted) {
        navigate({
          pathname: '/Request-a-review/Review-Form/step15',
          search: location.search,
        });
      }
      if ((window.location.pathname === '/Request-a-review/Review-Form/step2') && !isSubmitted) {
        navigate({
          pathname: '/Request-a-review/Review-Form/step3',
          search: location.search,
        });
      }
      if ((window.location.pathname === '/Request-a-review/Review-Form/step4') && !isSubmitted && formikValuesRef.current.values.SaveYesNo === 'true') {
        navigate({
          pathname: '/Request-a-review/Review-Form/step5',
          search: location.search,
        });
      }
      if ((window.location.pathname === '/Request-a-review/Review-Form/error') && !isSubmitted) {
        navigate({
          pathname: '/Request-a-review/Review-Form/error',
          search: location.search,
        });
      }
      if ((window.location.pathname === '/Request-a-review/Review-Form/step1') && !isSubmitted && formikValuesRef && formikValuesRef.current) {
        formikValuesRef.current.setFieldValue('OBLIGATION_NUMBER', '');
        formikValuesRef.current.setFieldTouched('OBLIGATION_NUMBER');
        formikValuesRef.current.setFieldValue('OFFENCE_DATE', '');
        formikValuesRef.current.setFieldValue('DRIVER_LICENCE', '');
      }
    };
    window.addEventListener('popstate', handlePopstate);
    // document.addEventListener('keyup', redirectUrl);
    return () => {
      window.removeEventListener('popstate', handlePopstate);
      //   document.removeEventListener('keydown', redirectUrl);
    };
  }, []);
  const postObligation = async (formikContext: any): Promise<void> => {
    setLoading(true);
    // remove whitespaces before and after obligation number
    const trimmedObligationNumber = formikContext.values.OBLIGATION_NUMBER.trim();
    formikValuesRef.current.setFieldValue('OBLIGATION_NUMBER', trimmedObligationNumber);
    const payload = {
      obligationNumber: trimmedObligationNumber,
    };
    const url = `${API_URL}sitecore/Review/ValidateObligation`;
    let response: any;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const responseBody = await response.json();
      setLoading(false);
      if (response.status === 400 && responseBody.Error) {
        setApiError(responseBody);
        return;
      }
      if (response.status === 422 && responseBody.Error) {
        setApiError(responseBody);
        return;
      }
      if (response.status === 200) {
        const nextPageUrl = `${pathNameLessLastSegment}${'/step2'}`;
        if (responseBody.obligationNumber) {
          formikValuesRef.current.setFieldValue('OBLIGATION_NUMBER', responseBody.obligationNumber);
        }
        setApiError({
          Error: '',
        });
        navigate(nextPageUrl);
        window.scrollTo(0, 0);
        return;
      }
      navToError(navigate, location);
    } catch (error) {
      navToError(navigate, location);
    }
  };

  const resetPFormDataValues = (partialFormApi: any, formikObject: any) => {
    if (partialFormApi && formikObject) {
      // clone data format like objectkey: [{},{}]
      if (
        Array.isArray(partialFormApi)
        && partialFormApi[0]
        && typeof partialFormApi[0] === 'object'
        && Array.isArray(formikObject)
        && (formikObject.length !== partialFormApi.length || partialFormApi.length === 0)
      ) {
        partialFormApi.forEach((val) => {
          type dynamicObj = {
            [key: string]: string;
          };
          const temp = {} as dynamicObj;
          Object.keys(val).forEach((key) => {
            temp[key] = val[key];
          });
          formikObject.push(temp);
        });
      }
      // handle scenario if formikObject is empty array , it's for ObjectKey:[] or string[]
      if (
        Array.isArray(partialFormApi)
        && partialFormApi[0]
        && typeof partialFormApi[0] === 'string'
        && Array.isArray(formikObject)
        && (formikObject.length !== partialFormApi.length || partialFormApi.length === 0)
      ) {
        partialFormApi.forEach((val) => {
          formikObject.push(val);
        });
      }
      if (partialFormApi && formikObject) {
        Object.keys(partialFormApi).forEach((key) => {
          if (key in formikObject) {
            if (typeof partialFormApi[key] === 'object' && !(partialFormApi[key] instanceof Date)) {
              if (partialFormAttachmentFields.includes(key) || arrayFieldsToBeCleared.includes(key)) {
                // eslint-disable-next-line no-param-reassign
                formikObject[key] = [];
              }
              resetPFormDataValues(partialFormApi[key], formikObject[key]);
            } else {
              // eslint-disable-next-line no-param-reassign
              formikObject[key] = partialFormApi[key];
            }
          }
        });
      }
    }
  };

  const createPartialForm = async (formikContext: any): Promise<void> => {
    setLoading(true);
    const { values } = formikContext;
    resetPFormDataValues(values, formikContext.values.FormData);
    const payload = {
      Attachments: '',
      CreateDate: '',
      DebtorID: `${formikContext.values.DebtorID}`,
      EMRemeinderSent: false,
      Email: formikContext.values.saveEmail,
      ExpiryDate: '',
      FormData: JSON.stringify({ a: '1', ...formikContext.values.FormData }),
      FormTypeID: 4,
      ID: 0,
      IsSubscribedEM: !!formikContext.values.saveEmail,
      IsSubscribedSMS: !!formikContext.values.saveMobile,
      Mobile: formikContext.values.saveMobile,
      Obligations: [
        {
          ID: 0,
          ObligationNO: formikContext.values.OBLIGATION_NUMBER,
          IsPrimary: true,
        },
      ],
      SMSRemeinderSent: false,
    };
    const url = `${API_URL}sitecore/PartialForms/CreatePartialForm`;
    let response: any;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const responseBody = await response.json();
      setLoading(false);
      if (response.status === 200) {
        if (responseBody.ID) {
          formikValuesRef.current.setFieldValue('PartialFormID', responseBody.ID);
          formikValuesRef.current.setFieldValue('Obligations', responseBody.Obligations);
        }
        if (formikContext.values.isDebtorTypeCompany) {
          // go to step 6
          navigate(`${pathNameLessLastSegment}/step6`);
        } else {
          navigateToNextStep(navigate, location, 'next');
        }
        return;
      }
      navToError(navigate, location);
    } catch (error) {
      console.log(error);
    }
  };
  const updatePartialFormData = async (formikContext: any): Promise<boolean> => {
    const { values } = formikContext;
    resetPFormDataValues(values, formikContext.values.FormData);
    const partialformPayload: iPartialFormData = {
      ID: formikContext.values.PartialFormID,
      Obligations: formikContext.values.Obligations,
      Attachments: [],
      FormData: JSON.stringify(formikContext.values.FormData), // {a: '1'},formikContext.values.FormData
      DebtorID: formikContext.values.DebtorID,
      FormTypeID: 4,
      Email: formikContext.values.saveEmail,
      Mobile: formikContext.values.saveMobile,
      IsSubscribedEM: !!formikContext.values.saveEmail,
      IsSubscribedSMS: !!formikContext.values.saveMobile,
      EMRemeinderSent: formikContext.values.EMRemeinderSent,
      SMSRemeinderSent: formikContext.values.SMSRemeinderSent,
      CreateDate: formikContext.values.CreateDate,
      ExpiryDate: formikContext.values.ExpiryDate,
    };
    try {
      const response = await savePartialForm(partialformPayload);
      if (!response) {
        navToError(navigate, location);
        return false;
      }
      return response;
    } catch (error) {
      console.log(error);
      navToError(navigate, location);
      return false;
    }
  };

  const step2NextPage = () => {
    navigateToNextStep(navigate, location, 'next');
  };

  const step3NextPage = (
    formikContext: any
  ) => {
    window.scrollTo(0, 0);
    if (formikContext.values.CanSave && formikContext.values.SaveYesNo !== 'true' && !formikContext.values.CheckAndConfirmPageLoaded) {
      navigateToNextStep(navigate, location, 'next');
      window.scrollTo(0, 0);
    } else if (formikContext.values.isDebtorTypeIndividual) {
      navigate(`${pathNameLessLastSegment}/step5`);
      window.scrollTo(0, 0);
    } else {
      navigate(`${pathNameLessLastSegment}/step6`);
      window.scrollTo(0, 0);
    }
  };

  const step4NextPage = (
    formikContext: any
    ) => {
      window.scrollTo(0, 0);
      if (formikContext.values.SaveYesNo === 'false' || getIn(formikContext.values, 'PartialFormID')) {
        if (formikContext.values.isDebtorTypeCompany) {
          // go to step 6
          navigate(`${pathNameLessLastSegment}/step6`);
        } else {
          navigateToNextStep(navigate, location, 'next');
        }
        return;
      }
      createPartialForm(formikContext);
  };

  const saveAndContinue = (
    formikContext: any
    ) => {
    window.scrollTo(0, 0);
    if (formikContext.values.SaveYesNo === 'true') {
      updatePartialFormData(formikContext);
    }
    navigateToNextStep(navigate, location, 'next');
  };

  const step6NextPage = (
    formikContext: any
    ) => {
    window.scrollTo(0, 0);
    if (formikContext.values.SaveYesNo === 'true') {
      updatePartialFormData(formikContext);
    }
    if (formikContext.values.selectTheOption.includes('SelectedGroundInfo4')) navigateToNextStep(navigate, location, 'next');
    else navigate(`${pathNameLessLastSegment}/step8`);
  };

  const step8NextPage = (
    formikContext: any
    ) => {
    window.scrollTo(0, 0);
    if (formikContext.values.SaveYesNo === 'true') {
      updatePartialFormData(formikContext);
    }
    if (formikContext.values.isThirdPartyAuthorised) navigateToNextStep(navigate, location, 'next');
    else navigate(`${pathNameLessLastSegment}/step10`);
  };

  const step10NextPage = (
    formikContext: any
    ) => {
    window.scrollTo(0, 0);
    if (formikContext.values.SaveYesNo === 'true') {
      updatePartialFormData(formikContext);
    }
    if (formikContext.values.isThirdPartyAuthorised || formikContext.values.isDebtorTypeCompany) navigate(`${pathNameLessLastSegment}/step12`);
    else navigateToNextStep(navigate, location, 'next');
  };
  const step11NextPage = (
    formikContext: any
    ) => {
    window.scrollTo(0, 0);
    if (formikContext.values.SaveYesNo === 'true') {
      updatePartialFormData(formikContext);
    }
    navigate(`${pathNameLessLastSegment}/step13`);
  };

  const getBasicInformation = async (formikContext: any): Promise<void> => {
    getBasicInfo(formikContext.values.OBLIGATION_NUMBER, FormType.REVIEW).then((responseBody) => {
      if (responseBody) {
        formikContext.setFieldValue('ObligationStatus', responseBody.ObligationStatus);
        formikContext.setFieldValue('TicketType', responseBody.TicketType);
        formikContext.setFieldValue('Status', responseBody.Status);
        formikContext.setFieldValue('DebtorType', responseBody.DebtorType);
        formikContext.setFieldValue('isPUEligible', responseBody.ValidForPersonUnaware);
        if (responseBody.DebtorType === 'Individual') {
          formikContext.setFieldValue('isDebtorTypeIndividual', true);
        }
        if (responseBody.DebtorType === 'Company') {
          formikContext.setFieldValue('isDebtorTypeCompany', true);
        }
        formikContext.setFieldValue('OffenceCode', responseBody.OffenceCode);
        formikContext.setFieldValue('isNominatable', responseBody.IsNominated);

        if (responseBody.FineStage === 'InternalReview') {
          formikContext.setFieldValue('isInternalReviewStage', true);
          formikContext.setFieldValue('isEnforcementReviewStage', false);
        } else if (responseBody.FineStage === 'EnforcementReview') {
          formikContext.setFieldValue('isEnforcementReviewStage', true);
          formikContext.setFieldValue('isInternalReviewStage', false);
        }
        // Navigation from step 2
        if (formikValuesRef.current.values.SaveYesNo === 'true') {
          if (responseBody.DebtorType === 'Individual') {
            setLoading(false);
            navigate(`${pathNameLessLastSegment}/step5`);
          } else {
            setLoading(false);
            navigate(`${pathNameLessLastSegment}/step6`);
          }
        } else if (responseBody.FineStage === 'EnforcementReview' || (responseBody.FineStage === 'InternalReview' && responseBody.DebtorType === 'Individual' && (responseBody.IsNominated || responseBody.Status === 'PenaltyReminder'))) {
          setLoading(false);
          step2NextPage();
        } else if (formikValuesRef.current.values.CanSave) {
          setLoading(false);
          navigate(`${pathNameLessLastSegment}/step4`);
        } else if (responseBody.DebtorType === 'Individual') {
          setLoading(false);
          navigate(`${pathNameLessLastSegment}/step5`);
        } else {
          setLoading(false);
          navigate(`${pathNameLessLastSegment}/step6`);
        }
      }
    });
  };

  const checkExistingForm = async (formikContext: any): Promise<void> => {
    setLoading(true);
    const payload = {
      ObligationNO: formikContext.values.OBLIGATION_NUMBER,
      formType: 'Reviews',
    };
    const url = `${API_URL}sitecore/PartialForms/CheckingExistingForm`;

    let response: any;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const responseBody = await response.json();
      if (response.status === 200) {
        if (responseBody.PartialFormID) formikValuesRef.current.setFieldValue('PartialFormID', responseBody.PartialFormID);
        if (responseBody.DebtorID) formikValuesRef.current.setFieldValue('DebtorID', responseBody.DebtorID, formikContext);
        if (responseBody.CanSave) formikValuesRef.current.setFieldValue('CanSave', responseBody.CanSave, formikContext);
        if (responseBody.CheckingExisting) {
          if (responseBody.CheckingExisting === 'yes') {
            formikValuesRef.current.setFieldValue('SaveYesNo', 'true', formikContext);
          }
        }
        if (responseBody.PartialFormID) {
          getPartialForm(responseBody.PartialFormID)
            .then((resBody) => {
              if (typeof resBody === 'string') {
                if (resBody === 'Unauthorised') {
                  navToError(navigate, location);
                }
              } else {
                const formData = JSON.parse(resBody.FormData);
                const keys = Object.keys(resBody);
                keys.forEach((key) => {
                  if (key === 'FormData') {
                    setFormDataValues(formData, formikValuesRef.current.values.FormData, formikValuesRef, 'FormData');
                    setCurrentFormikValues(formData, formikValuesRef.current.values, formikValuesRef);
                  } else if (resBody[key]) {
                    formikContext.setFieldValue(`${key}`, resBody[key]);
                  }
                });
              }
              setLoading(false);
            })
            .catch((e) => console.log(e));
        }
        window.scrollTo(0, 0);
      }
    } catch (error) {
      navToError(navigate, location);
    }
  };
  const getObOptions = async (formikContext: any) => {
    getObligationOptions(formikContext.values.OBLIGATION_NUMBER, FormType.REVIEW)
      .then((responseBody) => {
        if (responseBody && responseBody?.Authorised) {
          formikContext.setFieldValue('OBLIGATION_NUMBER', responseBody?.Authorised.Number);
          formikContext.setFieldValue('InfringementNoticeNumber', responseBody?.Authorised.InfringementNumber);
      }
    }).catch((e) => navToError(navigate, location));
  };

  const validateDebtor = async (formikContext: any): Promise<void> => {
    setLoading(true);
    // Backend api is checking date time for some reasons, time need to be 14:00:00
    const getOffenceDate = formikContext.values.OFFENCE_DATE && getDateForValidateDebtor(formikContext.values.OFFENCE_DATE.toLocaleDateString('en-AU'));
    const getHearingDate = formikContext.values.HEARING_DATE && getDateForValidateDebtor(formikContext.values.HEARING_DATE.toLocaleDateString('en-AU'));
    const getDOBDate = formikContext.values.DOB && getDateForValidateDebtor(formikContext.values.DOB.toLocaleDateString('en-AU'));
    // const dateObj = getTheDate.split('/');
    // const specificedDateString = `${dateObj[2]}-${dateObj[1]}-${dateObj[0]}`;
    const payload = {
      dateOfBirth: getDOBDate,
      hearingDate: getHearingDate,
      licenceNumber: formikContext.values.DRIVER_LICENCE,
      obligationNumber: formikContext.values.OBLIGATION_NUMBER,
      offenceDate: getOffenceDate,
      registrationNumber: formikContext.values.VEHICLE_REGISTRATION,
    };
    const url = `${API_URL}sitecore/Review/ValidateDebtor`;
    let response: any;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const responseBody = await response.json();
      setLoading(false);
      if (response.status === 400 && responseBody.Error) {
        setApiError(responseBody);
        return;
      }
      if (response.status === 200) {
        setApiError({
          Error: '',
        });
        // if (responseBody === '/step4') {
        //   step2NextPage();
        // }
      }
    } catch (error) {
      navToError(navigate, location);
    }
    getObOptions(formikContext);
    await checkExistingForm(formikContext);
    getBasicInformation(formikContext);
  };

  const formNavActions: iFormNavActions = {
    step1: {
      next: postObligation,
    },
    step2: {
      next: validateDebtor,
    },
    step3: {
      next: step3NextPage,
    },
    step4: {
      next: step4NextPage,
    },
    step5: {
      next: saveAndContinue,
    },
    step6: {
      next: step6NextPage,
    },
    step7: {
      next: saveAndContinue,
    },
    step8: {
      next: step8NextPage,
    },
    step9: {
      next: saveAndContinue,
    },
    step10: {
      next: step10NextPage,
    },
    step11: {
      next: step11NextPage,
    },
    step12: {
      next: saveAndContinue,
    },
    step13: {
      next: saveAndContinue,
    }
  };

  const clearApiError = () => {
    setApiError({
      Error: '',
    });
  };

  const displayErrorsCount = (errorCount: number, touchedFields: number): void => {
    if (touchedFields === 0) {
      setActiveError(true);
    } else if (errorCount > 0) {
      setActiveError(true);
    } else {
      setActiveError(false);
    }
  };
  const createPartialFormForSumbit = async (formValues: iReviewFormValues): Promise<string> => {
    formikValuesRef.current.setFieldValue('SaveYesNo', 'true');
    resetPFormDataValues(formikValuesRef.current.values, formValues.FormData);
    const payload = {
      Attachments: '',
      CreateDate: '',
      DebtorID: `${formValues.DebtorID}`,
      EMRemeinderSent: false,
      Email: '',
      ExpiryDate: '',
      FormData: JSON.stringify({ ...formValues.FormData, SaveYesNo: 'true' }),
      FormTypeID: 4,
      ID: 0,
      IsSubscribedEM: false,
      IsSubscribedSMS: false,
      Mobile: '',
      Obligations: [
        {
          ID: 0,
          ObligationNO: formValues.OBLIGATION_NUMBER,
          IsPrimary: true,
        },
      ],
      SMSRemeinderSent: false,
    };
    const url = `${API_URL}sitecore/PartialForms/CreatePartialForm`;
    let response: any;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const responseBody = await response.json();
      if (responseBody.status === 200) {
        return 'true';
      }
      return responseBody;
    } catch (error) {
      return `${error}`;
    }
  };
  const getReviewSubmissionEndpoint = (formValues: iReviewFormValues): string => {
    if (formValues.isInternalReviewStage) {
      return 'SubmitInternalReviewInfringement';
    }
    if (formValues.isEnforcementReviewStage && formValues.isDebtorTypeIndividual && !formValues.isThirdPartyAuthorised) {
      return 'SubmitEnforcementIndividual';
    }
    if (formValues.isEnforcementReviewStage && formValues.isDebtorTypeCompany) {
      return 'SubmitEnforcementCompany';
    }
    if (formValues.isEnforcementReviewStage && formValues.isThirdPartyAuthorised) {
      return 'SubmitEnforcementThirdParty';
    }
    return '';
  };

  const submitForm = async (formValues: iReviewFormValues): Promise<boolean> => {
    const submissionNumber = '007';
    const formSubmissionValues = getSubmissionPayload(formValues, submissionNumber);
    const submitPayload = {
      data: JSON.stringify(formSubmissionValues),
      obligationNo: formValues.OBLIGATION_NUMBER
    };

    const urlSubmission = `${API_URL}sitecore/Review/${getReviewSubmissionEndpoint(formValues)}`;
    let submitResponse = false;
      setLoading(true);
      submitResponse = await fetch(urlSubmission, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitPayload),
      })
      .then((formSubResponse) => {
        if (formSubResponse.status === 200) {
          if (formValues.SaveYesNo === 'true') {
            // having partial form, close it
            submitSavedPartialForm(formValues.PartialFormID);
          }
          formikValuesRef.current.setFieldValue('submitted', true);
          ReactGA.event({
            category: 'Review Form',
            action: 'Submit',
            label: 'Review Form',
          });
          // navigate to submission success
          return formSubResponse.json();
        }
        formikValuesRef.current.setFieldValue('submissionError', formSubResponse.statusText);
        if (formValues.SaveYesNo === 'false') {
          createPartialFormForSumbit(formValues);
        }
        navToError(navigate, location);
        return false;
      }).then((response) => {
        if (response) {
          if (response.receiptNo) {
            formikValuesRef.current.setFieldValue('submissionNo', response.receiptNo); // get submission number from API response
            return true;
          }
          navToError(navigate, location);
          return false;
        }
        return false;
      }).catch((error) => {
        formikValuesRef.current.setFieldValue('submissionError', error);
        if (formValues.SaveYesNo === 'false') {
          createPartialFormForSumbit(formValues);
        }
        console.log(error);
        navToError(navigate, location);
        return false;
      })
      .finally(() => {
        setLoading(false);
      });
      if (!submitResponse) return false;
      return true;
  };
  return (
    <Formik
      initialValues={defaultForm()}
      innerRef={formikValuesRef}
      validationSchema={ReviewSchema}
      onSubmit={(values: iReviewFormValues) => {
        submitForm(values);
      }}>
      {({ values }) => (
        <VicFinesContext.Provider value={{ countryList, setCountryList, tooltips, setTooltips, apiError, clearApiError }}>
          <FormContainer title="Review Form">
            {loading && <FormLoading />}
            {!loading && (
            <Form>
              <ProgressBar formMetaData={ReviewMetaData} />
              {activeError && <Alert title="Sorry there is a problem" icon={IssueIcon} color="red" message={<ErrorContent />} theme="error" />}
              <Routes>
                <Route path="/step0/:ob" element={<ReviewStep0 formikRef={formikValuesRef} />} />
                <Route path="/step1" element={<ReviewStep1 />} />
                <Route path="/step2" element={<ReviewStep2 />} />
                <Route path="/step3" element={<ReviewStep3 />} />
                <Route path="/step4" element={<ReviewStep4 />} />
                <Route path="/step5" element={<ReviewStep5 />} />
                <Route path="/step6" element={<ReviewStep6 />} />
                <Route path="/step7" element={<ReviewStep7 />} />
                <Route path="/step8" element={<ReviewStep8 />} />
                <Route path="/step9" element={<ReviewStep9 />} />
                <Route path="/step10" element={<ReviewStep10 />} />
                <Route path="/step11" element={<ReviewStep11 />} />
                <Route path="/step12" element={<ReviewStep12 />} />
                <Route path="/step13" element={<ReviewStep13 />} />
                <Route path="/step14" element={<ReviewStep14 />} />
                <Route path="/step15" element={<ReviewStep15 />} />
                <Route path="/error" element={<ReviewErrorPage />} />
                <Route path="/" element={<ReviewStep0 formikRef={formikValuesRef} />} />
              </Routes>

              {!(location.pathname === `${PATH_REVIEW}/step15` || location.pathname === `${PATH_REVIEW}/error`) && (
                <FormNavFooter
                  totalPages={15}
                  formNavActions={formNavActions}
                  submitPage={14}
                  submit={async () => submitForm(values)}
                  tooltips={tooltips}
                  displayErrorsCount={displayErrorsCount}
                  pageValidationKeys={ReviewMetaData.pagesSegments[currentStep].validationKeys(values)}
                  formType={FormType.REVIEW}
                  firstPageNo={0}
                />
              )}
            </Form>
          )}
          </FormContainer>
        </VicFinesContext.Provider>
      )}
    </Formik>
  );
}

export default Review;
